import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Camera,
  CheckCircle2,
  ClipboardPlus,
  LocateFixed,
  MapPin,
  Plus,
  Save,
  ShieldCheck,
  Sprout,
} from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import { executeOrQueue } from "@/offline/executeOrQueue";
import { queueMediaUpload } from "@/offline/outbox";
import { useOffline } from "@/offline/OfflineProvider";
import { toApiId } from "@/services/http/mappers";
import type {
  FieldVisit,
  VisitActivityDefinition,
  VisitActivityRecord,
  VisitFollowUp,
} from "@/types/domain";
import {
  captureCurrentPosition,
  loadFieldVisitContext,
  patchFieldVisitContext,
  type FieldVisitLocalContext,
} from "@/features/visits/fieldVisitLocal";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

type EvidenceType = "FARMER" | "FIELD" | "ACTIVITY" | "CHECK_OUT";

export default function VisitExecutionPage() {
  const { visitId = "" } = useParams();
  const navigate = useNavigate();
  const offline = useOffline();

  const [visit, setVisit] = useState<FieldVisit>();
  const [localContext, setLocalContext] = useState<FieldVisitLocalContext | undefined>(() => loadFieldVisitContext(visitId));
  const [activityCatalogue, setActivityCatalogue] = useState<VisitActivityDefinition[]>(localContext?.activityCatalogue || []);
  const [activities, setActivities] = useState<VisitActivityRecord[]>([]);
  const [followUps, setFollowUps] = useState<VisitFollowUp[]>([]);
  const [evidenceTypes, setEvidenceTypes] = useState<string[]>(localContext?.evidenceTypes || []);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(!localContext);
  const [working, setWorking] = useState(false);

  const [activityForm, setActivityForm] = useState({
    activityId: "",
    plotId: "",
    notes: "",
    quantity: "",
    unit: "",
  });
  const [followUpForm, setFollowUpForm] = useState({
    action: "",
    dueDate: "",
  });
  const [finalObservations, setFinalObservations] = useState(localContext?.observations || "");
  const [recommendations, setRecommendations] = useState("");
  const [inspectedPlotIds, setInspectedPlotIds] = useState<string[]>([]);
  const [checkoutPhoto, setCheckoutPhoto] = useState<File>();

  const isOffline = offline.simulatedOffline || !offline.online;

  const refreshOnline = useCallback(async () => {
    if (isOffline) return;
    const [visitRecord, activityRows, evidenceRows, followUpRows, catalogueRows] = await Promise.all([
      services.visits.get(visitId),
      services.visits.listActivities(visitId),
      services.visits.listEvidence(visitId),
      services.visits.listFollowUps(visitId),
      services.visits.listActivityCatalogue(),
    ]);
    if (!visitRecord) throw new Error("Field Visit was not found.");
    setVisit(visitRecord);
    setActivities(activityRows);
    setEvidenceTypes(evidenceRows.map((row) => row.evidenceType));
    setFollowUps(followUpRows);
    setActivityCatalogue(catalogueRows);
  }, [isOffline, visitId]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!isOffline) await refreshOnline();
        if (!localContext && active) {
          const fallbackVisit = await services.visits.get(visitId);
          if (fallbackVisit) setVisit(fallbackVisit);
        }
      } catch (reason) {
        if (!localContext && active) {
          setError(reason instanceof Error ? reason.message : "Field Visit could not be loaded.");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [isOffline, localContext, refreshOnline, visitId]);

  const plotIds = useMemo(
    () => localContext?.plotIds || visit?.plotIds || (visit?.plotId ? [visit.plotId] : []),
    [localContext, visit],
  );
  const plotNames = useMemo(
    () => localContext?.plotNames || visit?.plotNames || (visit?.plotName ? [visit.plotName] : []),
    [localContext, visit],
  );

  const farmerName = localContext?.farmerName || visit?.farmerName || "Farmer";
  const farmName = localContext?.farmName || visit?.farmName || "Farm";
  const assignedStaffName = localContext?.assignedStaffName || visit?.assignedStaffName || "Assigned staff";
  const status = visit?.status || "IN_PROGRESS";

  const updateLocal = (changes: Partial<FieldVisitLocalContext>) => {
    const updated = patchFieldVisitContext(visitId, changes);
    if (updated) setLocalContext(updated);
  };

  const queueOrUploadEvidence = async (type: EvidenceType, file: File, plotId?: string) => {
    const position = await captureCurrentPosition();
    const evidenceId = crypto.randomUUID();
    const capturedAt = new Date().toISOString();
    const query = {
      captured_at: capturedAt,
      latitude: position.latitude,
      longitude: position.longitude,
      gps_accuracy_metres: position.accuracyMetres,
      plot_id: plotId ? toApiId(plotId) : undefined,
      device_identifier: offline.deviceId,
      created_offline: isOffline,
      local_created_at: capturedAt,
      client_generated_id: evidenceId,
    };

    if (isOffline) {
      await queueMediaUpload({
        id: evidenceId,
        entityType: "FIELD_VISIT_EVIDENCE",
        entityId: visitId,
        path: `/visits/${toApiId(visitId)}/evidence/${type}`,
        file,
        query,
      });
    } else {
      try {
        await services.visits.uploadEvidence(visitId, {
          clientGeneratedId: evidenceId,
          evidenceType: type,
          file,
          capturedAt,
          latitude: position.latitude,
          longitude: position.longitude,
          gpsAccuracyMetres: position.accuracyMetres,
          plotId,
          deviceIdentifier: offline.deviceId,
          createdOffline: false,
          localCreatedAt: capturedAt,
        });
      } catch (reason) {
        if (!navigator.onLine) {
          await queueMediaUpload({
            id: evidenceId,
            entityType: "FIELD_VISIT_EVIDENCE",
            entityId: visitId,
            path: `/visits/${toApiId(visitId)}/evidence/${type}`,
            file,
            query,
          });
        } else {
          throw reason;
        }
      }
    }

    const nextTypes = Array.from(new Set([...evidenceTypes, type]));
    setEvidenceTypes(nextTypes);
    updateLocal({ evidenceTypes: nextTypes });
    return position;
  };

  const addEvidence = async (type: Exclude<EvidenceType, "CHECK_OUT">, file?: File) => {
    if (!file) return;
    setWorking(true);
    try {
      await queueOrUploadEvidence(type, file, plotIds[0]);
      toast.success(`${type.replace("_", " ").toLowerCase()} photo recorded`, {
        description: isOffline ? "Evidence is safely queued on this device." : "GPS and capture time were recorded.",
      });
      if (!isOffline) await refreshOnline();
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Photo evidence could not be recorded.");
    } finally {
      setWorking(false);
    }
  };

  const addActivity = async (event: FormEvent) => {
    event.preventDefault();
    if (!activityForm.activityId) {
      toast.error("Choose an activity.");
      return;
    }
    const activityId = crypto.randomUUID();
    const performedAt = new Date().toISOString();
    const payload = {
      activity_id: toApiId(activityForm.activityId),
      plot_id: activityForm.plotId ? toApiId(activityForm.plotId) : undefined,
      performed_at: performedAt,
      notes: activityForm.notes.trim() || undefined,
      quantity: activityForm.quantity ? Number(activityForm.quantity) : undefined,
      unit: activityForm.unit.trim() || undefined,
      client_generated_id: activityId,
      device_identifier: offline.deviceId,
      created_offline: isOffline,
    };

    setWorking(true);
    try {
      const result = await executeOrQueue({
        offline: isOffline,
        request: {
          entityType: "FIELD_VISIT_ACTIVITY",
          entityId: activityId,
          method: "POST",
          path: `/visits/${toApiId(visitId)}/activities`,
          payload,
        },
        execute: () => services.visits.addActivity(visitId, {
          activityId: activityForm.activityId,
          plotId: activityForm.plotId || undefined,
          performedAt,
          notes: activityForm.notes.trim() || undefined,
          quantity: activityForm.quantity ? Number(activityForm.quantity) : undefined,
          unit: activityForm.unit.trim() || undefined,
          clientGeneratedId: activityId,
          deviceIdentifier: offline.deviceId,
          createdOffline: false,
        }),
      });

      const definition = activityCatalogue.find((item) => item.id === activityForm.activityId);
      if (result.mode === "SYNCED") setActivities((current) => [...current, result.value]);
      updateLocal({ activityCount: (localContext?.activityCount || activities.length) + 1 });
      setActivityForm({ activityId: "", plotId: "", notes: "", quantity: "", unit: "" });
      toast.success("Activity recorded", {
        description: `${definition?.activityName || "Field activity"}${result.mode === "QUEUED" ? " queued for sync." : " saved."}`,
      });
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Activity could not be recorded.");
    } finally {
      setWorking(false);
    }
  };

  const addFollowUp = async (event: FormEvent) => {
    event.preventDefault();
    if (!followUpForm.action.trim() || !followUpForm.dueDate) {
      toast.error("Follow-up action and due date are required.");
      return;
    }
    const followUpId = crypto.randomUUID();
    const payload = {
      action: followUpForm.action.trim(),
      due_date: followUpForm.dueDate,
      assigned_staff_id: visit?.conductedByStaffId ? toApiId(visit.conductedByStaffId) : undefined,
      client_generated_id: followUpId,
      device_identifier: offline.deviceId,
      created_offline: isOffline,
    };

    setWorking(true);
    try {
      const result = await executeOrQueue({
        offline: isOffline,
        request: {
          entityType: "FIELD_VISIT_FOLLOW_UP",
          entityId: followUpId,
          method: "POST",
          path: `/visits/${toApiId(visitId)}/follow-ups`,
          payload,
        },
        execute: () => services.visits.addFollowUp(visitId, {
          action: followUpForm.action.trim(),
          dueDate: followUpForm.dueDate,
          assignedStaffId: visit?.conductedByStaffId,
          clientGeneratedId: followUpId,
          deviceIdentifier: offline.deviceId,
          createdOffline: false,
        }),
      });

      if (result.mode === "SYNCED") setFollowUps((current) => [...current, result.value]);
      updateLocal({ followUpCount: (localContext?.followUpCount || followUps.length) + 1 });
      setFollowUpForm({ action: "", dueDate: "" });
      toast.success("Follow-up added", {
        description: result.mode === "QUEUED" ? "Task is queued for synchronization." : "Task is linked to this visit.",
      });
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Follow-up could not be recorded.");
    } finally {
      setWorking(false);
    }
  };

  const toggleInspectedPlot = (plotId: string, checked: boolean) => {
    setInspectedPlotIds((current) =>
      checked ? Array.from(new Set([...current, plotId])) : current.filter((id) => id !== plotId),
    );
  };

  const completeVisit = async () => {
    const requiredEvidence = ["CHECK_IN", "FARMER", "FIELD"];
    const missing = requiredEvidence.filter((type) => !evidenceTypes.includes(type));
    if (missing.length) {
      toast.error(`Required evidence missing: ${missing.join(", ").split("_").join(" ")}.`);
      return;
    }
    if (inspectedPlotIds.length === 0) {
      toast.error("Mark at least one Plot as actually inspected.");
      return;
    }
    if (!finalObservations.trim()) {
      toast.error("Final field observations are required.");
      return;
    }
    if (!checkoutPhoto) {
      toast.error("A check-out photo is required.");
      return;
    }

    setWorking(true);
    try {
      const position = await queueOrUploadEvidence("CHECK_OUT", checkoutPhoto, inspectedPlotIds[0]);
      const completedAt = new Date().toISOString();
      const completionPayload = {
        completed_at: completedAt,
        latitude: position.latitude,
        longitude: position.longitude,
        gps_accuracy_metres: position.accuracyMetres,
        inspected_plot_ids: inspectedPlotIds.map(toApiId),
        observations: finalObservations.trim(),
        recommendations: recommendations.trim() || undefined,
      };

      const result = await executeOrQueue({
        offline: isOffline,
        request: {
          entityType: "FIELD_VISIT_COMPLETE",
          entityId: visitId,
          method: "POST",
          path: `/visits/${toApiId(visitId)}/complete`,
          payload: completionPayload,
        },
        execute: () => services.visits.complete(visitId, {
          completedAt,
          latitude: position.latitude,
          longitude: position.longitude,
          gpsAccuracyMetres: position.accuracyMetres,
          inspectedPlotIds,
          observations: finalObservations.trim(),
          recommendations: recommendations.trim() || undefined,
        }),
      });

      toast.success(result.mode === "QUEUED" ? "Visit completed offline" : "Field visit completed", {
        description: result.mode === "QUEUED"
          ? "Evidence will sync before the completion command."
          : "Check-out GPS, evidence, findings and Plot coverage are saved.",
      });
      navigate(result.mode === "SYNCED" ? `/visits/${visitId}` : "/visits");
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Field Visit could not be completed.");
    } finally {
      setWorking(false);
    }
  };

  if (error) return <ErrorState message={error} />;
  if (loading) return <LoadingState label="Loading active Field Visit" />;
  if (!localContext && !visit) return <ErrorState message="Field Visit context is unavailable." />;

  const displayedActivities = activities.length || localContext?.activityCount || 0;
  const displayedFollowUps = followUps.length || localContext?.followUpCount || 0;

  return (
    <div className="space-y-6" data-testid="visit-execution-page">
      <PageHeader
        eyebrow="Field Visit · In the field"
        title={farmerName}
        description={`${farmName} · ${plotNames.join(", ") || "Selected Plot"}`}
        backTo="/visits"
      />

      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={status} testId="visit-execution-status" />
        <span className="text-sm text-muted-foreground">
          Assigned: <span className="font-semibold text-foreground">{assignedStaffName}</span>
        </span>
        {(isOffline || localContext) && (
          <span className="rounded-full border px-2.5 py-1 text-xs text-muted-foreground">
            {isOffline ? "Offline capture active" : "Field context retained locally"}
          </span>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card><CardContent className="p-5"><ClipboardPlus className="size-5 text-primary" /><p className="mt-3 text-2xl font-semibold">{displayedActivities}</p><p className="text-sm text-muted-foreground">Activities recorded</p></CardContent></Card>
        <Card><CardContent className="p-5"><Camera className="size-5 text-primary" /><p className="mt-3 text-2xl font-semibold">{evidenceTypes.length}</p><p className="text-sm text-muted-foreground">Evidence types captured</p></CardContent></Card>
        <Card><CardContent className="p-5"><CheckCircle2 className="size-5 text-primary" /><p className="mt-3 text-2xl font-semibold">{displayedFollowUps}</p><p className="text-sm text-muted-foreground">Follow-ups created</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Camera className="size-4 text-primary" />Required field evidence</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Check-in is already captured. Before completion, record the farmer and the field/garden. Each photo gets its own field timestamp and GPS.</p>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <EvidenceCapture label="Farmer photo" complete={evidenceTypes.includes("FARMER")} disabled={working} onCapture={(file) => addEvidence("FARMER", file)} />
            <EvidenceCapture label="Field / garden photo" complete={evidenceTypes.includes("FIELD")} disabled={working} onCapture={(file) => addEvidence("FIELD", file)} />
            <EvidenceCapture label="Additional activity photo" complete={evidenceTypes.includes("ACTIVITY")} disabled={working} optional onCapture={(file) => addEvidence("ACTIVITY", file)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Sprout className="size-4 text-primary" />What was done?</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={addActivity} className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Activity / KPI</Label>
              <Select value={activityForm.activityId} onValueChange={(value: string) => setActivityForm((current) => ({ ...current, activityId: value }))}>
                <SelectTrigger><SelectValue placeholder="Choose activity" /></SelectTrigger>
                <SelectContent>{activityCatalogue.map((item) => <SelectItem key={item.id} value={item.id}>{item.activityName} · {item.category}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Plot</Label>
              <Select value={activityForm.plotId} onValueChange={(value: string) => setActivityForm((current) => ({ ...current, plotId: value }))}>
                <SelectTrigger><SelectValue placeholder="Whole visit / choose Plot" /></SelectTrigger>
                <SelectContent>{plotIds.map((plotId, index) => <SelectItem key={plotId} value={plotId}>{plotNames[index] || `Plot ${index + 1}`}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label htmlFor="activity-quantity">Quantity</Label><Input id="activity-quantity" type="number" min="0" step="0.01" value={activityForm.quantity} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setActivityForm((current) => ({ ...current, quantity: event.target.value }))} placeholder="Optional" /></div>
            <div className="space-y-2"><Label htmlFor="activity-unit">Unit</Label><Input id="activity-unit" value={activityForm.unit} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setActivityForm((current) => ({ ...current, unit: event.target.value }))} placeholder="kg, litres, bags, unit..." /></div>
            <div className="space-y-2 md:col-span-2"><Label htmlFor="activity-notes">Activity notes</Label><Textarea id="activity-notes" value={activityForm.notes} onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setActivityForm((current) => ({ ...current, notes: event.target.value }))} placeholder="What was done, applied, delivered, demonstrated, checked, or trained?" /></div>
            <div className="md:col-span-2 flex justify-end"><Button type="submit" disabled={working}><Plus className="mr-2 size-4" />Add activity</Button></div>
          </form>

          {activities.length > 0 && <div className="mt-5 space-y-2 border-t pt-4">{activities.map((activity) => <div key={activity.id} className="rounded-lg border p-3 text-sm"><p className="font-semibold">{activity.activityName || "Field activity"}</p><p className="text-xs text-muted-foreground">{new Date(activity.performedAt).toLocaleString()}{activity.notes ? ` · ${activity.notes}` : ""}</p></div>)}</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><ClipboardPlus className="size-4 text-primary" />Follow-up / to-do</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={addFollowUp} className="grid gap-4 md:grid-cols-[1fr_220px_auto] md:items-end">
            <div className="space-y-2"><Label htmlFor="follow-up-action">Action required</Label><Input id="follow-up-action" value={followUpForm.action} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFollowUpForm((current) => ({ ...current, action: event.target.value }))} placeholder="e.g. Return with fungicide; recheck survival count" /></div>
            <div className="space-y-2"><Label htmlFor="follow-up-date">Due date</Label><Input id="follow-up-date" type="date" value={followUpForm.dueDate} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFollowUpForm((current) => ({ ...current, dueDate: event.target.value }))} /></div>
            <Button type="submit" disabled={working}><Plus className="mr-2 size-4" />Add</Button>
          </form>

          {followUps.length > 0 && <div className="mt-5 space-y-2 border-t pt-4">{followUps.map((item) => <div key={item.id} className="flex items-start justify-between gap-3 rounded-lg border p-3 text-sm"><div><p className="font-semibold">{item.action}</p><p className="text-xs text-muted-foreground">Due {item.dueDate}</p></div><StatusBadge status={item.status} testId={`visit-follow-up-${item.id}`} /></div>)}</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><MapPin className="size-4 text-primary" />Close the field visit</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div>
            <p className="font-semibold">Which Plot(s) were actually inspected?</p>
            <p className="mt-1 text-sm text-muted-foreground">Only check Plot(s) physically inspected. A selected but missed Plot must remain unchecked.</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">{plotIds.map((plotId, index) => <label key={plotId} className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm"><input type="checkbox" className="size-4" checked={inspectedPlotIds.includes(plotId)} onChange={(event: React.ChangeEvent<HTMLInputElement>) => toggleInspectedPlot(plotId, event.target.checked)} /><span>{plotNames[index] || `Plot ${index + 1}`}</span></label>)}</div>
          </div>

          <div className="space-y-2"><Label htmlFor="final-observations">Final field observations</Label><Textarea id="final-observations" value={finalObservations} onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setFinalObservations(event.target.value)} placeholder="What was found on the farmer's Plot(s) today?" /></div>
          <div className="space-y-2"><Label htmlFor="recommendations">Recommendations</Label><Textarea id="recommendations" value={recommendations} onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setRecommendations(event.target.value)} placeholder="Agronomic recommendations or next actions communicated to the farmer" /></div>

          <div className="rounded-xl border p-4">
            <p className="font-semibold">Mandatory check-out photo</p>
            <p className="mt-1 text-sm text-muted-foreground">Capture fresh evidence before leaving. GPS and check-out time are recorded when the visit is completed.</p>
            <Input className="mt-3" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCheckoutPhoto(event.target.files?.[0])} />
            {checkoutPhoto && <p className="mt-2 text-xs font-medium text-primary">{checkoutPhoto.name} ready</p>}
          </div>

          <div className="flex flex-col gap-3 rounded-xl border bg-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3"><LocateFixed className="mt-0.5 size-5 text-primary" /><div><p className="font-semibold">GPS check-out & complete</p><p className="text-sm text-muted-foreground">Completion is rejected if required photo evidence or inspected Plot truth is missing.</p></div></div>
            <Button type="button" onClick={completeVisit} disabled={working} data-testid="visit-complete-button">{working ? <LocateFixed className="mr-2 size-4 animate-pulse" /> : <Save className="mr-2 size-4" />}Complete visit</Button>
          </div>
        </CardContent>
      </Card>

      {isOffline && <Card className="border-warning/30 bg-warning/[0.03]"><CardContent className="flex items-start gap-3 p-4 text-sm"><ShieldCheck className="mt-0.5 size-4 text-warning" /><p><span className="font-semibold">Offline field capture:</span> activities, tasks and photo blobs stay on this device. Sync order is visit → activities/tasks → evidence → completion.</p></CardContent></Card>}

      <div className="text-center text-xs text-muted-foreground"><Link to={`/visits/${visitId}`} className="text-primary hover:underline">Open visit record</Link></div>
    </div>
  );
}

function EvidenceCapture({
  label,
  complete,
  disabled,
  optional = false,
  onCapture,
}: {
  label: string;
  complete: boolean;
  disabled: boolean;
  optional?: boolean;
  onCapture: (file?: File) => void;
}) {
  return (
    <label className={`block rounded-xl border p-4 ${complete ? "border-primary/25 bg-primary/[0.035]" : ""}`}>
      <div className="flex items-center justify-between gap-2"><p className="font-semibold">{label}</p>{complete && <CheckCircle2 className="size-4 text-primary" />}</div>
      <p className="mt-1 text-xs text-muted-foreground">{complete ? "Captured" : optional ? "Optional" : "Required before completion"}</p>
      <Input className="mt-3" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" disabled={disabled} onChange={(event: React.ChangeEvent<HTMLInputElement>) => { const file = event.target.files?.[0]; if (file) onCapture(file); event.currentTarget.value = ""; }} />
    </label>
  );
}
